import { supabase } from '@/integrations/supabase/client';

type GateEventType = 'impression' | 'signup' | 'pro_click';

// Generate or retrieve session ID for anonymous tracking
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('cn_gate_session');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('cn_gate_session', sessionId);
  }
  return sessionId;
}

export async function trackGateEvent(
  eventType: GateEventType,
  productId: string | undefined,
  triggerType?: 'timer' | 'scroll',
  timeOnGateMs?: number
): Promise<void> {
  try {
    await supabase.from('gate_events').insert({
      event_type: eventType,
      product_id: productId,
      session_id: getSessionId(),
      trigger_type: triggerType,
      time_on_gate_ms: timeOnGateMs,
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    // Silent fail - don't break UX for analytics
    console.error('Failed to track gate event:', error);
  }
}

export async function getGateAnalytics(days: number = 7) {
  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data, error } = await supabase
    .from('gate_events')
    .select('*')
    .gte('created_at', since.toISOString());

  if (error) throw error;

  const impressions = data.filter(e => e.event_type === 'impression');
  const signups = data.filter(e => e.event_type === 'signup');
  const proClicks = data.filter(e => e.event_type === 'pro_click');

  const conversions = signups.length + proClicks.length;
  const conversionRate = impressions.length > 0 
    ? (conversions / impressions.length) * 100 
    : 0;

  const timerImpressions = impressions.filter(e => e.trigger_type === 'timer').length;
  const scrollImpressions = impressions.filter(e => e.trigger_type === 'scroll').length;

  const avgTimeToConvert = signups.length > 0
    ? signups.reduce((sum, e) => sum + (e.time_on_gate_ms || 0), 0) / signups.length / 1000
    : 0;

  return {
    impressions: impressions.length,
    signups: signups.length,
    proClicks: proClicks.length,
    conversions,
    conversionRate: Math.round(conversionRate * 10) / 10,
    abandonmentRate: Math.round((100 - conversionRate) * 10) / 10,
    avgTimeToConvert: Math.round(avgTimeToConvert * 10) / 10,
    timerImpressions,
    scrollImpressions,
  };
}
